import { useState } from 'react'
import { useApp } from '@/store/AppContext'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Car, Bike, Wrench, AlertCircle, ArrowRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { CheckInForm } from '@/components/tickets/CheckInForm'
import { CheckoutSheet } from '@/components/tickets/CheckoutSheet'
import { ParkingSpace, SpaceStatus } from '@/lib/types'
import { useToast } from '@/hooks/use-toast'

export default function SpaceMap() {
  const { spaces, tickets, updateSpaceStatus, isOperationMode } = useApp()
  const { toast } = useToast()
  const [selectedSpace, setSelectedSpace] = useState<ParkingSpace | null>(null)
  const [checkoutTicketId, setCheckoutTicketId] = useState<string | null>(null)
  const [isCheckInOpen, setIsCheckInOpen] = useState(false)

  const handleSpaceClick = (space: ParkingSpace) => {
    setSelectedSpace(space)
  }

  const handleStatusChange = (status: SpaceStatus) => {
    if (selectedSpace) {
      updateSpaceStatus(selectedSpace.id, status)
      toast({
        title: 'Status Atualizado',
        description: `Vaga ${selectedSpace.number} marcada como ${status}.`,
      })
      setSelectedSpace(null)
    }
  }

  const getSpaceColor = (status: SpaceStatus) => {
    switch (status) {
      case 'available':
        return 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
      case 'occupied':
        return 'bg-red-50 border-red-300 text-red-800 hover:bg-red-100'
      case 'reserved':
        return 'bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100'
      case 'maintenance':
        return 'bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100'
    }
  }

  const activeTickets = tickets.filter((t) => t.status === 'active')

  return (
    <div className="space-y-6 animate-fade-in-up pb-10 h-full flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mapa de Vagas</h1>
          <p className="text-muted-foreground">
            Visualize e gerencie a ocupação do pátio em tempo real.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 text-sm font-medium bg-card p-3 rounded-lg border shadow-sm">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-400"></span> Livre
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-400"></span> Ocupada
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-400"></span> Reservada
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-400"></span>{' '}
            Manutenção
          </div>
        </div>
      </div>

      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardContent className="p-4 md:p-6 overflow-y-auto flex-1 bg-muted/10">
          <div
            className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3 ${isOperationMode ? 'gap-4' : ''}`}
          >
            {spaces.map((space) => {
              const ticket = space.ticketId
                ? activeTickets.find((t) => t.id === space.ticketId)
                : null
              return (
                <button
                  key={space.id}
                  onClick={() => handleSpaceClick(space)}
                  className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${getSpaceColor(space.status)} ${isOperationMode ? 'h-28' : 'h-24'} shadow-sm`}
                >
                  <span className="absolute top-1 left-2 text-xs font-bold opacity-60">
                    {space.number}
                  </span>
                  {space.type === 'moto' ? (
                    <Bike className="h-6 w-6 mb-1 opacity-70" />
                  ) : space.type === 'vip' ? (
                    <span className="text-[10px] font-black uppercase mb-1 opacity-70">
                      VIP
                    </span>
                  ) : space.type === 'pcd' ? (
                    <span className="text-[10px] font-black uppercase mb-1 opacity-70 bg-primary text-primary-foreground px-1 rounded">
                      PCD
                    </span>
                  ) : (
                    <Car className="h-6 w-6 mb-1 opacity-70" />
                  )}

                  {space.status === 'occupied' && ticket && (
                    <span className="font-black text-xs sm:text-sm tracking-tighter truncate w-full px-1">
                      {ticket.plate}
                    </span>
                  )}
                  {space.status === 'maintenance' && (
                    <Wrench className="h-5 w-5" />
                  )}
                  {space.status === 'reserved' && (
                    <AlertCircle className="h-5 w-5" />
                  )}
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedSpace && !isCheckInOpen}
        onOpenChange={(o) => !o && setSelectedSpace(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>
              Vaga {selectedSpace?.number} - {selectedSpace?.type.toUpperCase()}
            </DialogTitle>
            <DialogDescription>Gerencie o status desta vaga.</DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {selectedSpace?.status === 'available' && (
              <>
                <Button
                  className="w-full h-12 text-lg font-bold"
                  onClick={() => setIsCheckInOpen(true)}
                >
                  Registrar Entrada na Vaga
                </Button>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => handleStatusChange('reserved')}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    Reservar Vaga
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleStatusChange('maintenance')}
                    className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                  >
                    Em Manutenção
                  </Button>
                </div>
              </>
            )}

            {selectedSpace?.status === 'occupied' && (
              <>
                <div className="bg-muted p-6 rounded-xl mb-4 text-center border shadow-inner">
                  <p className="text-sm text-muted-foreground mb-1">
                    Veículo Ocupante
                  </p>
                  <p className="text-4xl font-black tracking-widest">
                    {
                      activeTickets.find((t) => t.id === selectedSpace.ticketId)
                        ?.plate
                    }
                  </p>
                </div>
                <Button
                  className="w-full h-14 text-lg font-bold"
                  onClick={() => {
                    setCheckoutTicketId(selectedSpace.ticketId || null)
                    setSelectedSpace(null)
                  }}
                >
                  Fazer Check-out <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </>
            )}

            {(selectedSpace?.status === 'reserved' ||
              selectedSpace?.status === 'maintenance') && (
              <>
                <div className="bg-muted p-6 rounded-xl mb-4 text-center border">
                  <AlertCircle className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-lg font-medium">Vaga indisponível</p>
                  <p className="text-sm text-muted-foreground">
                    Status:{' '}
                    {selectedSpace.status === 'reserved'
                      ? 'Reservada'
                      : 'Em Manutenção'}
                  </p>
                </div>
                <Button
                  className="w-full h-12 text-lg font-bold"
                  onClick={() => handleStatusChange('available')}
                >
                  Liberar Vaga para Uso
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isCheckInOpen} onOpenChange={setIsCheckInOpen}>
        <DialogContent className="sm:max-w-[500px] p-6 border bg-background shadow-xl rounded-xl">
          <CheckInForm
            initialSpaceId={selectedSpace?.id}
            onSuccess={() => {
              setIsCheckInOpen(false)
              setSelectedSpace(null)
            }}
            isDialog={true}
          />
        </DialogContent>
      </Dialog>

      {checkoutTicketId && (
        <CheckoutSheet
          ticketId={checkoutTicketId}
          onClose={() => setCheckoutTicketId(null)}
        />
      )}
    </div>
  )
}
